import { genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { TimeUnit } from "@redotech/redo-common-model/time";
import {
  AutoHoldType,
  PlaceHoldActionStep,
  PlaceHoldUntilActionStep,
} from "@redotech/redo-flows-common/oms/actions";
import { RedoShopifyFulfillmentHoldReason } from "@redotech/redo-fulfillment-group-common/fulfillment-hold-reason";
import { ShopifyFulfillmentHoldDisplayNames } from "@redotech/redo-fulfillment-group-common/shopify-fulfillment-schemas";
import { useTranslation } from "@redotech/redo-i18n";
import { DeprecatedDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-date-picker";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from ".";

export const HoldDaysInput = memo(function HoldDaysInput({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<PlaceHoldActionStep>;
  setStep: (step: PartialWithRequiredType<PlaceHoldActionStep>) => void;
}) {
  const { t } = useTranslation();
  const setHoldDays = useHandler((value: number) => {
    setStep({
      ...step,
      holdOptions: {
        ...step.holdOptions,
        holdType: AutoHoldType.Duration,
        duration: Math.floor(value),
        timeUnit: step.holdOptions?.timeUnit || TimeUnit.DAY,
      },
    });
  });

  const setTimeUnit = useHandler(
    ({ item }: { item: RedoListItem<TimeUnit | "indefinite"> }) => {
      if (item.value === "indefinite") {
        setStep({
          ...step,
          holdOptions: {
            ...step.holdOptions,
            holdType: AutoHoldType.Indefinite,
            duration: undefined,
            timeUnit: undefined,
          },
        });
      } else {
        setStep({
          ...step,
          holdOptions: {
            ...step.holdOptions,
            holdType: AutoHoldType.Duration,
            timeUnit: item.value,
          },
        });
      }
    },
  );

  const duration =
    step.holdOptions?.holdType === AutoHoldType.Duration
      ? step.holdOptions?.duration
      : undefined;

  const timeUnit = step.holdOptions?.timeUnit || TimeUnit.DAY;
  const isIndefinite = step.holdOptions?.holdType === AutoHoldType.Indefinite;

  const timeUnitOptions: RedoListItem<TimeUnit | "indefinite">[] = useMemo(
    () => [
      {
        id: TimeUnit.MINUTE,
        value: TimeUnit.MINUTE,
        type: "text",
        text: t("minutes", { count: duration || 0 }),
      },
      {
        id: TimeUnit.HOUR,
        value: TimeUnit.HOUR,
        type: "text",
        text: t("hours", { count: duration || 0 }),
      },
      {
        id: TimeUnit.DAY,
        value: TimeUnit.DAY,
        type: "text",
        text: t("days", { count: duration || 0 }),
      },
      {
        id: "indefinite",
        value: "indefinite",
        type: "text",
        text: t("indefinitely"),
      },
    ],
    [duration, t],
  );

  return (
    <Flex align="center" flex={1} gap="sm">
      {!isIndefinite && (
        <RedoIncrementDecrement
          setValue={setHoldDays}
          size="small"
          step={1}
          value={duration}
        />
      )}

      <RedoSingleSelectDropdownInput<TimeUnit | "indefinite">
        options={timeUnitOptions}
        optionSelected={setTimeUnit}
        selectedItem={timeUnitOptions.find(
          (o) => o.id === (isIndefinite ? "indefinite" : timeUnit),
        )}
        size="xs"
      />
    </Flex>
  );
});

export const HoldDateInput = memo(function HoldDateInput({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<PlaceHoldUntilActionStep>;
  setStep: (step: PartialWithRequiredType<PlaceHoldUntilActionStep>) => void;
}) {
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  const setHoldDate = useHandler((date: Date | null) => {
    if (date) {
      // Set the time to 9:00 AM
      date.setHours(9, 0, 0, 0);

      setStep({
        ...step,
        holdOptions: { ...step.holdOptions, holdType: AutoHoldType.Date, date },
      });
    }
  });

  return (
    <Flex flex={1}>
      <DeprecatedDatePicker
        buttonSize="xs"
        date={
          step.holdOptions?.date instanceof Date
            ? step.holdOptions.date
            : tomorrow
        }
        minDate={tomorrow}
        setDate={setHoldDate}
      />
    </Flex>
  );
});

export const HoldDetailsInput = genericMemo(function HoldDetailsInput<
  T extends PlaceHoldActionStep | PlaceHoldUntilActionStep,
>({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<T>;
  setStep: (step: PartialWithRequiredType<T>) => void;
}) {
  const { t } = useTranslation();
  const holdReasonOptions: RedoListItem<RedoShopifyFulfillmentHoldReason>[] =
    Object.values(RedoShopifyFulfillmentHoldReason).map((reason) => ({
      id: reason,
      value: reason,
      type: "text",
      text: ShopifyFulfillmentHoldDisplayNames[reason],
    }));

  const setHoldReason = useHandler(
    ({ item }: { item: RedoListItem<RedoShopifyFulfillmentHoldReason> }) => {
      setStep({
        ...step,
        holdOptions: { ...step.holdOptions, holdReason: item.value },
      });
    },
  );

  const setReasonNotes = useHandler((reasonNotes: string) => {
    setStep({ ...step, holdOptions: { ...step.holdOptions, reasonNotes } });
  });

  return (
    <Flex dir="column" gap="sm">
      <RedoSingleSelectDropdownInput<RedoShopifyFulfillmentHoldReason>
        label={t("Reason")}
        options={holdReasonOptions}
        optionSelected={setHoldReason}
        placeholder={t("Select a reason")}
        selectedItem={holdReasonOptions.find(
          (o) => o.id === step.holdOptions?.holdReason,
        )}
        size="xs"
      />
      <RedoTextInput
        label={t("Hold notes")}
        placeholder={t("Optional notes")}
        setValue={setReasonNotes}
        value={step.holdOptions?.reasonNotes || ""}
      />
    </Flex>
  );
});
